import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { IEntityState } from 'app/projects/entity/src/lib/interfaces/entity.state.interface';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import {
    CreateExperienceWidget,
    CreateExperienceWidgetFailure,
    CreateExperienceWidgetSuccess,
    DeleteExperienceWidget,
    DeleteExperienceWidgetFailure,
    DeleteExperienceWidgetSuccess,
    FetchExperienceWidget,
    FetchExperienceWidgetFailure,
    FetchExperienceWidgets,
    FetchExperienceWidgetsFailure,
    FetchExperienceWidgetsSuccess,
    FetchExperienceWidgetSuccess,
    SetExperienceWidget,
    SetExperienceWidgets,
    UnsetExperienceWidget,
    UnsetExperienceWidgets,
    UpdateExperienceWidget,
    UpdateExperienceWidgetFailure,
    UpdateExperienceWidgetSuccess,
} from '../actions';
import { AddExperienceWidgets } from '../actions/add-entities.action';
import { ExperienceWidgetApiService } from '../services/experience-widget.api-service';
import { ExperienceWidget } from './experience-widget.model';
import { ExperienceWidgetStateModel } from './experience-widget.state-model';

@State<ExperienceWidgetStateModel>({
    name: 'experienceWidget',
    defaults: new ExperienceWidgetStateModel(),
})
@Injectable()
export class ExperienceWidgetState extends EntityState<ExperienceWidget> implements IEntityState<ExperienceWidget> {
    static sortProperties: string[] = ['name'];

    @Selector()
    static getEntity(state: ExperienceWidgetStateModel, id: string = state.item): ExperienceWidget {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntities(state: ExperienceWidgetStateModel): ExperienceWidget[] {
        if (!state.map) {
            return null;
        }

        const toReturn = Array.from(state.map.values());

        toReturn.sort(MaxBrainUtils.sortCompareFn(ExperienceWidgetState.sortProperties));

        return toReturn;
    }

    constructor(private _experienceService: ExperienceWidgetApiService) {
        super(_experienceService, 0);

        //this._propstoSearch = MaxBrainExperienceWidget.props_to_search;
    }

    @Action(AddExperienceWidgets)
    addEntities(ctx: StateContext<ExperienceWidgetStateModel>, action: AddExperienceWidgets): void {
        this._addEntities(ctx, action);
    }

    @Action(SetExperienceWidget)
    setEntity(ctx: StateContext<ExperienceWidgetStateModel>, action: SetExperienceWidget): void {
        this._setEntity(ctx, action, AddExperienceWidgets);
    }

    @Action(UnsetExperienceWidget)
    unsetEntity(ctx: StateContext<ExperienceWidgetStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(CreateExperienceWidget)
    createEntity(ctx: StateContext<ExperienceWidgetStateModel>, action: CreateExperienceWidget): void {
        this._createEntity(ctx, action, CreateExperienceWidgetSuccess, CreateExperienceWidgetFailure, AddExperienceWidgets).then(() => {});
    }

    @Action(UpdateExperienceWidget)
    updateEntity(ctx: StateContext<ExperienceWidgetStateModel>, action: UpdateExperienceWidget): void {
        this._updateEntity(ctx, action, UpdateExperienceWidgetSuccess, UpdateExperienceWidgetFailure, AddExperienceWidgets).then(() => {});
    }

    @Action(DeleteExperienceWidget)
    deleteEntity(ctx: StateContext<ExperienceWidgetStateModel>, action: DeleteExperienceWidget): void {
        this._deleteEntity(ctx, action, DeleteExperienceWidgetSuccess, DeleteExperienceWidgetFailure).then(() => {});
    }

    @Action(FetchExperienceWidget)
    fetchEntity(ctx: StateContext<ExperienceWidgetStateModel>, action: FetchExperienceWidget): void {
        this._fetchEntity(ctx, action, FetchExperienceWidgetSuccess, FetchExperienceWidgetFailure, AddExperienceWidgets, SetExperienceWidget).then(() => {});
    }

    @Action(FetchExperienceWidgetSuccess)
    fetchEntitySuccess(ctx: StateContext<ExperienceWidgetStateModel>, action: FetchExperienceWidgetSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchExperienceWidgetFailure)
    fetchEntityFailure(ctx: StateContext<ExperienceWidgetStateModel>, action: FetchExperienceWidgetFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(SetExperienceWidgets)
    setEntities(ctx: StateContext<ExperienceWidgetStateModel>, action: SetExperienceWidgets): void {
        this._setEntities(ctx, action);
    }

    @Action(UnsetExperienceWidgets)
    unsetAll(ctx: StateContext<ExperienceWidgetStateModel>): void {
        ctx.setState(new ExperienceWidgetStateModel());
    }

    @Action(FetchExperienceWidgets)
    fetchEntities(ctx: StateContext<ExperienceWidgetStateModel>, { payload }: FetchExperienceWidgets): void {
        this._fetchEntities(
            ctx,
            FetchExperienceWidgetsSuccess,
            FetchExperienceWidgetsFailure,
            SetExperienceWidgets,
            this._experienceService.getWidgetsByExperienceId(payload.experienceId)
        ).then(() => {});
    }

    @Action(FetchExperienceWidgetsSuccess)
    fetchEntitiesSuccess(ctx: StateContext<ExperienceWidgetStateModel>): void {
        this._fetchEntitiesSuccess(ctx);
    }

    @Action(FetchExperienceWidgetsFailure)
    fetchEntitiesFailure(ctx: StateContext<ExperienceWidgetStateModel>): void {
        this._fetchEntitiesFailure(ctx);
    }
}
